import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ExternalLink, Sparkles, Layers, Plus } from "lucide-react";
import type { CoursePreview, WorkspaceItem } from "../types";
import {
  CustomCourseRequestModal,
  CourseTrackOutlineModal,
  LinkCourseToWorkspaceModal,
} from "../components/modals";
import { useOrgDashboard } from "../context/OrgDashboardContext";

export interface CoursesPageProps {
  courses: CoursePreview[];
}

export const CoursesPage: React.FC<CoursesPageProps> = ({ courses }) => {
  const navigate = useNavigate();
  const { slug, workspaces, documents, loadWorkspaces, stats, loadStats } = useOrgDashboard();

  // Modals state
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [selectedTrackCourse, setSelectedTrackCourse] = useState<CoursePreview | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [targetWorkspace, setTargetWorkspace] = useState<WorkspaceItem | null>(null);

  const activeWorkspace = workspaces && workspaces.length > 0 ? workspaces[0] : null;

  const handleOpenRequestModal = () => {
    setIsRequestModalOpen(true);
  };

  const handleOpenTrackModal = (course: CoursePreview) => {
    setSelectedTrackCourse(course);
  };

  const handleOpenAssignModal = (_course?: CoursePreview) => {
    const ws = activeWorkspace;
    if (ws) {
      setTargetWorkspace(ws);
      setIsAssignModalOpen(true);
    } else {
      navigate(`/org/${slug}/dashboard?tab=workspaces`);
    }
  };

  if (!courses || courses.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center shadow-xs">
        <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <h3 className="text-base font-bold text-slate-900 mb-1">No Sponsored Courses Available</h3>
        <p className="text-xs text-slate-500 max-w-md mx-auto mb-6 leading-relaxed">
          Your organization does not currently have any sponsored catalog courses assigned. You can submit a request for a custom training program tailored to your team&apos;s internal SOPs and learning goals.
        </p>
        <button
          onClick={handleOpenRequestModal}
          className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs font-bold rounded-xl shadow-xs transition inline-flex items-center gap-2 cursor-pointer"
        >
          <Sparkles className="w-4 h-4 text-purple-200" />
          Request Custom Course
        </button>
        <CustomCourseRequestModal
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          slug={slug}
          workspace={activeWorkspace ? { slug: activeWorkspace.slug, name: activeWorkspace.name } : null}
          workspaceDocuments={(documents || []).map((d) => ({
            id: d.id,
            title: d.title,
            document_type: d.document_type || "SOP",
          }))}
          onSuccess={async () => {
            await loadWorkspaces(true);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-xs">
        <div>
          <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            Sponsored Enterprise Tracks
          </h3>
          <p className="text-xs text-slate-500 max-w-xl">
            Curated curriculum tracks sponsored for your team members. Inspect detailed syllabi, lesson outlines, and concepts before assigning them to workspaces.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenRequestModal}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition cursor-pointer"
          >
            <Sparkles className="w-4 h-4 text-purple-200" />
            Request Custom Course
          </button>
          <div className="px-3.5 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-xl text-xs font-bold font-mono">
            {courses.length} Sponsored Track{courses.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <CustomCourseRequestModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        slug={slug}
        workspace={activeWorkspace ? { slug: activeWorkspace.slug, name: activeWorkspace.name } : null}
        workspaceDocuments={(documents || []).map((d) => ({
          id: d.id,
          title: d.title,
          document_type: d.document_type || "SOP",
        }))}
        onSuccess={async () => {
          await loadWorkspaces(true);
        }}
      />

      {/* Course Track & Outline Modal */}
      <CourseTrackOutlineModal
        isOpen={Boolean(selectedTrackCourse)}
        onClose={() => setSelectedTrackCourse(null)}
        slug={slug}
        course={selectedTrackCourse}
        onAssignToWorkspace={(course) => {
          handleOpenAssignModal(course);
        }}
      />

      {/* Link Course to Workspace Modal */}
      {targetWorkspace && (
        <LinkCourseToWorkspaceModal
          isOpen={isAssignModalOpen}
          onClose={() => {
            setIsAssignModalOpen(false);
            setTargetWorkspace(null);
          }}
          slug={slug}
          workspace={targetWorkspace}
          assignedCourseIds={[]}
          sponsoredCourses={stats?.sponsored_courses || []}
          onSuccess={async () => {
            await loadWorkspaces(true);
            await loadStats(true);
          }}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {courses.map((course) => (
          <div
            key={course.id}
            className="bg-white border border-slate-200 rounded-3xl overflow-hidden flex flex-col justify-between hover:border-slate-300 hover:shadow-md transition group shadow-xs"
          >
            <div>
              <div className="h-44 bg-slate-100 relative overflow-hidden flex items-center justify-center">
                {course.cover_image_url ? (
                  <img
                    src={course.cover_image_url}
                    alt={course.title}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/images/courses/python-ai.png";
                    }}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                  />
                ) : (
                  <BookOpen className="w-12 h-12 text-slate-400" />
                )}
                <span className="absolute top-3 right-3 px-2.5 py-1 bg-white/90 backdrop-blur-md border border-slate-200 text-indigo-700 rounded-lg text-[10px] font-bold uppercase tracking-wider shadow-xs">
                  Enterprise Track
                </span>
              </div>

              <div className="p-6">
                <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition truncate">
                  {course.title}
                </h3>
                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed mb-4">
                  {course.description || "Comprehensive corporate curriculum with AI personalized lessons."}
                </p>

                <div className="flex items-center gap-4 text-xs text-slate-400 font-medium">
                  <span>{course.total_modules} Modules</span>
                  <span>•</span>
                  <span>{course.total_lessons} Lessons</span>
                </div>
              </div>
            </div>

            <div className="p-6 pt-0 space-y-2">
              {/* Primary: View Track & Outline */}
              <button
                type="button"
                onClick={() => handleOpenTrackModal(course)}
                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition shadow-xs active:scale-95 cursor-pointer"
              >
                <Layers className="w-4 h-4" />
                View Track & Outline
              </button>

              {/* Secondary actions: Assign to Workspace & Learner preview */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleOpenAssignModal(course)}
                  className="flex-1 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 text-[11px] font-semibold rounded-lg flex items-center justify-center gap-1.5 transition cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5 text-slate-500" />
                  Assign
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/courses/${course.uuid}`)}
                  title="Preview Course as Learner"
                  className="px-2.5 py-2 bg-slate-50 hover:bg-slate-100 text-slate-500 hover:text-slate-800 border border-slate-200 text-[11px] rounded-lg flex items-center justify-center transition cursor-pointer"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
